import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { Spinner, Stack } from '../ui';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from './auth';

/**
 * Session guard for the authenticated area. The router's `beforeLoad` only redirects a
 * *missing* token; a present-but-invalid token (expired, or invalidated by a deploy that
 * rotated `JWT_SECRET`) passes that check and reaches here. When `me` resolves as a 401,
 * `useAuth().isAuthenticated` is false while a token is still present — rendering the
 * protected children in that state crashes (`useWorkspaceId()` throws "No workspace
 * selected"). Instead we clear the stale token and bounce to /login.
 *
 * States:
 * - loading (`me` in flight) → centered spinner (matches the AppShell spinner).
 * - loaded but not authenticated → clear the stale token + navigate to /login (in an
 *   effect — navigation during render is invalid), render a spinner so the protected
 *   children never mount. `/login` is outside this guard, so no redirect loop.
 * - authenticated → render children.
 */
export function RequireAuthenticated({ children }: { children: ReactNode }): React.ReactElement {
  const { isLoading, isAuthenticated, signOut } = useAuth();
  const navigate = useNavigate();

  const shouldRedirect = !isLoading && !isAuthenticated;

  useEffect(() => {
    if (!shouldRedirect) return;
    // `signOut()` clears the token (api.logout sets it to null even on a 401) + query
    // cache. Guard against the logout call rejecting on a dead token so the redirect
    // always happens.
    void signOut().catch(() => {});
    void navigate({ to: '/login' });
  }, [shouldRedirect, signOut, navigate]);

  // Mount the protected children ONLY once `me` has resolved AND the session is valid.
  // `isAuthenticated` is optimistically true whenever a token is present (including while
  // `me` is still loading), but `workspaces` is empty until it resolves — so rendering
  // children during the load window would make a child's `useWorkspaceId()` throw
  // "No workspace selected" even for a valid session. Gate on `!isLoading` too.
  if (!isLoading && isAuthenticated) return <>{children}</>;

  return (
    <Stack align="center" justify="center" style={{ minHeight: '100vh' }}>
      <Spinner size="lg" />
    </Stack>
  );
}
